import { ITeamDetails } from '@shared/types';
import TeamLogo from 'app/scenes/statsmanager/components/teamlogo/TeamLogo';
import React, { useCallback, useEffect, useState } from 'react';
import { ITeamStandings, ITeamWeeklyStats, StandingsSortBy } from 'types';

import styles from './DivisionTeam.module.scss';

type DivisionTeamProps = {
  className?: string;
  team: ITeamDetails;
  teamWeeklyResult: ITeamStandings;
  highlighted?: boolean;
  mode?: StandingsSortBy;
  isEliminated?: boolean;
};

type ExpandedTeamWeeklyStats = ITeamWeeklyStats & {
  inDivisionTotalRecord: string;
};

const DivisionTeam: React.FC<DivisionTeamProps> = ({
  className = '',
  team,
  teamWeeklyResult,
  highlighted = false,
  mode = 'division',
  isEliminated = false,
}) => {
  const eliminatedCls = isEliminated ? styles.DivisionTeamEliminated : '';
  const baseCls = `${styles.DivisionTeamContainer} ${className} ${highlighted ? styles.DivisionTeamHighlight : ''}`;
  const [streakString, setStreakString] = useState<string>('0');
  const [weeklyStats, setWeeklyStats] = useState<ExpandedTeamWeeklyStats>({
    wins: 0,
    losses: 0,
    ties: 0,
    pct: 0,
    pf: 0,
    pa: 0,
    homeRecord: '0-0-0',
    awayRecord: '0-0-0',
    streak: 0,
    inDivisionHomeRecord: '0-0-0',
    inDivisionAwayRecord: '0-0-0',
    inDivisionTotalRecord: '0-0-0',
  });

  const formatRecord = (teamRecord: string) => {
    const [wins, losses, ties] = teamRecord.split('-');
    return ties === '0' ? `${wins}-${losses}` : `${wins}-${losses}-${ties}`;
  };

  const addHomeAwayRecords = useCallback((homeRecord: string, awayRecord: string) => {
    const [homeWins, homeLosses, homeTies] = homeRecord.split('-');
    const [awayWins, awayLosses, awayTies] = awayRecord.split('-');
    const wins = parseInt(homeWins) + parseInt(awayWins);
    const losses = parseInt(homeLosses) + parseInt(awayLosses);
    const ties = parseInt(homeTies) + parseInt(awayTies);
    return formatRecord(`${wins}-${losses}-${ties}`);
  }, []);

  useEffect(() => {
    if (teamWeeklyResult) {
      const isTie = teamWeeklyResult.streak > 0 && teamWeeklyResult.streak < 1;
      const isWin = teamWeeklyResult.streak >= 1;
      const isZero = teamWeeklyResult.streak === 0;
      const streak = isTie
        ? `${teamWeeklyResult.streak * 100}T`
        : isWin
          ? `${teamWeeklyResult.streak}W`
          : isZero
            ? '---'
            : `${Math.abs(teamWeeklyResult.streak)}L`;
      setStreakString(streak);
      setWeeklyStats({
        wins: teamWeeklyResult.wins,
        losses: teamWeeklyResult.losses,
        ties: teamWeeklyResult.ties,
        pct: teamWeeklyResult.pct,
        pf: teamWeeklyResult.pf,
        pa: teamWeeklyResult.pa,
        homeRecord: formatRecord(teamWeeklyResult.homeRecord),
        awayRecord: formatRecord(teamWeeklyResult.awayRecord),
        streak: teamWeeklyResult.streak,
        inDivisionHomeRecord: formatRecord(teamWeeklyResult.inDivisionHomeRecord),
        inDivisionAwayRecord: formatRecord(teamWeeklyResult.inDivisionAwayRecord),
        inDivisionTotalRecord: addHomeAwayRecords(
          teamWeeklyResult.inDivisionHomeRecord,
          teamWeeklyResult.inDivisionAwayRecord
        ),
      });
    }
  }, [addHomeAwayRecords, teamWeeklyResult]);

  return (
    <div className={baseCls}>
      <div key={team.teamID} className={`${styles.DivisionTeam} ${eliminatedCls}`}>
        <TeamLogo className={styles.DivisionTeamLogo} teamName={team.teamAbrv} iconType="v1" />
        <span className={styles.DivisionTeamName}>{team.teamAbrv}</span>
        <span className={styles.DivisionTeamStats}>
          <span>{weeklyStats.wins}</span>
          <span>{weeklyStats.losses}</span>
          <span>{weeklyStats.ties}</span>
          <span>{weeklyStats.pct.toFixed(3).replace('0.', '.')}</span>
          <span>{weeklyStats.pf}</span>
          <span>{weeklyStats.pa}</span>
          <span>{weeklyStats.homeRecord}</span>
          <span>{weeklyStats.awayRecord}</span>
          {mode === 'wildcard' && <span>{weeklyStats.inDivisionHomeRecord}</span>}
          {mode === 'wildcard' && <span>{weeklyStats.inDivisionAwayRecord}</span>}
          {mode === 'division' && <span>{weeklyStats.inDivisionTotalRecord}</span>}
          <span>{streakString}</span>
        </span>
      </div>
    </div>
  );
};

export default DivisionTeam;
